import {materialConnection} from "#material/nodes/base/connection/shorthand"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {TexImage} from "#material/nodes/texture/tex-image/tex-image"
import type {TextureFromSetSockets} from "#material/nodes/texture/texture-from-set/definition"
import {textureFromSetDefinition} from "#material/nodes/texture/texture-from-set/definition"
import {createMappingNode} from "#material/nodes/texture/texture-set/helpers"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {DataResolver} from "#material/validation/data-resolver/index"
import {registerMaterialGraphNode} from "#material/register"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import {textureTypeFromLegacy} from "#material/nodes/base/material/validation/texture"
import type {TextureType} from "#material/types/texture"
import type {MaterialNodeFlattenedReturns} from "#material/sockets/parameters"

@registerMaterialGraphNode
export class TextureFromSet extends MaterialNode<TextureFromSetSockets> {
    public override get definition() {
        return textureFromSetDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TextureFromSetSockets>> => {
        const graph = await this.buildTextureFromSetGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TextureFromSetSockets>> => {
        const graph = await this.buildTextureFromSetGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override flatten = async (resolve: DataResolver) => {
        return this.buildTextureFromSetGraph(resolve)
    }

    async buildTextureFromSetGraph(dataResolver: DataResolver): Promise<MaterialNodeFlattenedReturns<TextureFromSetSockets>> {
        if (!this.parameters.settings.textureSetRevisionId) throw new Error("Texture set revision ID is required")
        if (!this.parameters.settings.mapAssignmentTextureType) throw new Error("Map assignment texture type is required")

        const slot = textureTypeFromLegacy(this.parameters.settings.mapAssignmentTextureType)
        const mapAssignment = await dataResolver.textureSetRevisionSlot({
            id: this.parameters.settings.textureSetRevisionId,
            slot,
        })
        if (!mapAssignment) {
            throw new Error(`No map assignment found for texture set revision ID: ${this.parameters.settings.textureSetRevisionId}`)
        }
        const {widthCm, heightCm} = mapAssignment

        if (widthCm === undefined || heightCm === undefined) {
            throw new Error(`Width and height must be defined for texture set revision slot: ${this.parameters.settings.textureSetRevisionId}`)
        }

        const uv = this.parameters.inputs.uv
        const mapping = createMappingNode(uv?.clone({cloneSubNode: () => false}), [widthCm, heightCm, 1])

        const texImage = new TexImage({
            parameters: {
                inputs: {
                    vector: materialConnection(mapping, "vector"),
                },
                settings: {
                    extension: this.parameters.settings.extension ?? "REPEAT",
                    interpolation: this.parameters.settings.interpolation ?? "Closest",
                    projection: this.parameters.settings.projection ?? "FLAT",
                    dataObjectLegacyId: mapAssignment.dataObject.legacyId,
                    textureSetRevisionId: this.parameters.settings.textureSetRevisionId,
                },
            },
        })

        return {
            color: materialConnection(texImage, "color"),
            alpha: materialConnection(texImage, "alpha"),
        }
    }

    get mapAssignmentTextureType(): TextureType | null {
        const legacyTextureType = this.parameters.settings.mapAssignmentTextureType
        if (legacyTextureType) {
            return textureTypeFromLegacy(legacyTextureType)
        }
        return null
    }
}
